import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories, getVideosByCategory, getCategoryBySlug, videos } from '@/mocks/videos';

type SortOption = 'latest' | 'popular' | 'views';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<SortOption>('latest');
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    } else {
      setActiveSlug('all');
    }
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const categoryVideos = useMemo(() => {
    let result = activeSlug === 'all' ? [...videos] : getVideosByCategory(activeSlug);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.tags.some((t) => t.toLowerCase().includes(q)) ||
          v.author.toLowerCase().includes(q) ||
          v.categoryName.toLowerCase().includes(q)
      );
    }
    result = [...result].sort((a, b) => {
      if (sortBy === 'latest') return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
      if (sortBy === 'popular') return b.likes - a.likes;
      if (sortBy === 'views') return b.views - a.views;
      return 0;
    });
    return result;
  }, [activeSlug, searchQuery, sortBy]);

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const pageTitle = searchQuery
    ? `搜索"${searchQuery}" - 91视频网`
    : `${category?.name || '全部'}视频 - 91视频网`;
  const pageDesc = category
    ? `${category.description}，共${category.videoCount}个视频等您发现。91视频网提供海量高清视频在线观看。`
    : '浏览91视频网全部视频内容，海量高清资源免费观看。';

  const sortOptions: { value: SortOption; label: string; icon: string }[] = [
    { value: 'latest', label: '最新发布', icon: 'ri-time-line' },
    { value: 'popular', label: '最多点赞', icon: 'ri-heart-line' },
    { value: 'views', label: '最多观看', icon: 'ri-eye-line' },
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`91视频网,${category?.name || ''}视频,在线观看,高清视频`}
        canonicalUrl={`https://www.91video.com/category/${activeSlug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb & Header */}
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.name || '全部分类' },
                ]}
              />

              <div className="mt-4">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {searchQuery ? `搜索: "${searchQuery}"` : category?.name || '全部分类'}
                </h1>
                {!searchQuery && category && (
                  <p className="text-sm text-foreground-500 max-w-2xl">{category.description}</p>
                )}
                {searchQuery && (
                  <p className="text-sm text-foreground-500">
                    找到 {categoryVideos.length} 个相关视频
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            {/* Category Tabs */}
            {!searchQuery && (
              <div className="mb-6 overflow-x-auto">
                <div className="flex items-center gap-2 min-w-max">
                  <button
                    onClick={() => handleCategoryClick('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.slug)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-xs`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sort & Count */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{categoryVideos.length}</strong> 个视频
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${opt.icon} text-xs`}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Video Grid */}
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {categoryVideos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    thumbnail={video.thumbnail}
                    duration={video.duration}
                    views={video.views}
                    publishDate={video.publishDate}
                    author={video.author}
                    authorAvatar={video.authorAvatar}
                    categoryName={video.categoryName}
                    categorySlug={video.categoryId}
                    quality={video.quality}
                    slug={video.slug}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-line text-3xl"></i>
                </div>
                <h3 className="text-lg font-medium text-foreground-700 mb-2">未找到相关视频</h3>
                <p className="text-sm text-foreground-500 mb-4">试试其他关键词或分类吧</p>
                <button
                  onClick={() => setActiveSlug('all')}
                  className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览全部视频
                </button>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}